import React, { useEffect, useState } from 'react'
import { App } from '../../types/global'
import { useTranslation } from 'react-i18next'
import { PrimaryLinkButton } from '../button'
import AppsDropdown from './apps-dropdown'

import { useLazyQuery, useQuery } from '@apollo/client'
import { getAllAppsQuery, findProductsQuery } from '../../queries'
import { AllApps } from '../../queries/types/AllApps'
import { FindProducts, FindProductsVariables } from '../../queries/types/FindProducts'

import '../../utils'
import { ProductRow } from './row'
import { EmptyState, LoadingRows, PageHeader, TableCard, TableHead, TableMessageRow } from '../ui'

const COLUMN_COUNT = 6

export function ProductListing(): JSX.Element {
    const {t} = useTranslation()
    const { data: apps } = useQuery<AllApps>(getAllAppsQuery)
    const [ fetchProducts, fetchProductsResult ] = useLazyQuery<FindProducts, FindProductsVariables>(findProductsQuery, {
        fetchPolicy: 'cache-and-network'
    })
    const [app, setApp] = useState<App|null>(null)

    useEffect(() => {
        if (app) {
            if (fetchProductsResult.refetch){
                fetchProductsResult.refetch({
                    appID: app.id
                })
            } else {
                fetchProducts({
                    variables: {
                        appID: app.id
                    }
                })
            }
        }
    }, [app])

    useEffect(() => {
        if (app == null && apps && apps.apps.length > 0) {
            setApp(apps.apps[0])
        }
    }, [apps])

    const products = fetchProductsResult.data?.findProducts

    function tableBody() {
        if (!products && fetchProductsResult.loading) {
            return <TableMessageRow colSpan={COLUMN_COUNT}><LoadingRows message={t('products.loading')} /></TableMessageRow>
        }
        if (app && products && products.length === 0) {
            return (
                <TableMessageRow colSpan={COLUMN_COUNT}>
                    <EmptyState
                        title={t('products.empty.title')}
                        subtitle={t('products.empty.subtitle')}
                        action={<PrimaryLinkButton to='/products/create'>{t('shared.button.label.create')}</PrimaryLinkButton>}
                    />
                </TableMessageRow>
            )
        }
        if (!app) {
            return <TableMessageRow colSpan={COLUMN_COUNT}><EmptyState title={t('shared.state.select-app-title')} subtitle={t('products.select-app.subtitle')} /></TableMessageRow>
        }
        return products?.map((product) => <ProductRow key={product.id} product={product} app={app} />)
    }

    return (
        <>
            <PageHeader title={t('products.title')} subtitle={app ? t('products.head.footer') : undefined}>
                {apps ? <AppsDropdown items={apps.apps} selected={app} onDidSelect={setApp} className='w-full sm:w-56' /> : null}
                <PrimaryLinkButton to='/products/create'>{t('shared.button.label.create')}</PrimaryLinkButton>
            </PageHeader>
            <TableCard>
                <TableHead columns={[
                    t('products.table.titles.id'),
                    t('products.table.titles.title'),
                    t('products.table.titles.mmk'),
                    t('products.table.titles.usd'),
                    t('products.table.titles.create-order'),
                    <span key='actions' className='sr-only'>Actions</span>,
                ]} />
                <tbody>{tableBody()}</tbody>
            </TableCard>
        </>
    )
}
